/**
 * AURA (Accessibility, User Responsiveness, and Assistance) Core Type Definitions
 */

// Profile names
export type ProfileName =
  | 'seizureSafe'
  | 'visionImpaired'
  | 'adhdFriendly'
  | 'cognitiveDisability'
  | 'keyboardNavigation'
  | 'screenReader';

// Content settings
export interface ContentSettings {
  contentScaling: number; // 100-200
  readableFont: boolean;
  highlightTitles: boolean;
  highlightLinks: boolean;
  textMagnifier: boolean;
  fontSizing: number; // pixels, 16 = browser default
  lineHeight: number; // 1.0-3.0
  letterSpacing: number; // 0-5
  textAlignment: 'default' | 'left' | 'center' | 'right';
}

// Color settings
export interface ColorSettings {
  darkContrast: boolean;
  lightContrast: boolean;
  highContrast: boolean;
  highSaturation: boolean;
  lowSaturation: boolean;
  monochrome: boolean;
  saturation: number; // 0-200, 100 = normal
  textColor: string | null;
  titleColor: string | null;
  backgroundColor: string | null;
}

// Orientation settings
export interface OrientationSettings {
  muteSounds: boolean;
  hideImages: boolean;
  readMode: boolean;
  readingGuide: boolean;
  stopAnimations: boolean;
  readingMask: boolean;
  highlightHover: boolean;
  highlightFocus: boolean;
  bigCursors: boolean;
  bigBlackCursor: boolean;
  bigWhiteCursor: boolean;
}

// Combined settings
export interface AuraSettings {
  content: ContentSettings;
  color: ColorSettings;
  orientation: OrientationSettings;
}

// Deep-partial variant used by updateSettings / activateProfile
export type PartialAuraSettings = {
  content?: Partial<ContentSettings> | undefined;
  color?: Partial<ColorSettings> | undefined;
  orientation?: Partial<OrientationSettings> | undefined;
};

// Widget state
export interface AuraState {
  activeProfile: ProfileName | null;
  settings: AuraSettings;
  isOpen: boolean;
  isHidden: boolean;
  isAuthenticated: boolean;
  isLoading: boolean;
}

// Profile definition
export interface ProfileDefinition {
  name: string;
  description: string;
  icon: string;
  /** Play a notification sound when this profile is toggled on */
  playSound?: boolean;
  /** Another profile that should always toggle in sync with this one */
  linkedWith?: ProfileName;
  settings: Partial<{
    content: Partial<ContentSettings>;
    color: Partial<ColorSettings>;
    orientation: Partial<OrientationSettings>;
  }>;
}

// Configuration
export interface AuraConfig {
  appId: string;
  position?: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';
  theme?: 'light' | 'dark' | 'auto';
  language?: string;
  zIndex?: number;
  enabledFeatures?: {
    profiles?: boolean;
    contentAdjustments?: boolean;
    colorAdjustments?: boolean;
    orientationAdjustments?: boolean;
    aiRemediation?: boolean;
    analytics?: boolean;
    statement?: boolean;
  };
  remediationApiUrl?: string;
  preferenceSyncApiUrl?: string;
  analyticsApiUrl?: string;
  logApiUrl?: string;
  authTokenProvider?: () => Promise<string | null>;
  statement?: StatementConfig;
  onReady?: () => void;
  onSettingsChange?: (settings: AuraSettings) => void;
  onError?: (error: Error) => void;
}

export interface StatementConfig {
  companyName: string;
  companyUrl: string;
  contactEmail: string;
  contactPhone?: string;
  complianceTarget?: string;
  lastReviewDate?: string;
  knownLimitations?: string[];
  additionalContent?: string;
}

// Event types
export type AuraEventType =
  | 'settings:change'
  | 'profile:activate'
  | 'profile:deactivate'
  | 'panel:open'
  | 'panel:close'
  | 'remediation:start'
  | 'remediation:complete'
  | 'ready'
  | 'error';

export interface AuraEvent<T = unknown> {
  type: AuraEventType;
  payload?: T;
  timestamp: number;
}

// Default settings factory
export function createDefaultSettings(): AuraSettings {
  return {
    content: {
      contentScaling: 100,
      readableFont: false,
      highlightTitles: false,
      highlightLinks: false,
      textMagnifier: false,
      fontSizing: 16,
      lineHeight: 1.5,
      letterSpacing: 0,
      textAlignment: 'default',
    },
    color: {
      darkContrast: false,
      lightContrast: false,
      highContrast: false,
      highSaturation: false,
      lowSaturation: false,
      monochrome: false,
      saturation: 100,
      textColor: null,
      titleColor: null,
      backgroundColor: null,
    },
    orientation: {
      muteSounds: false,
      hideImages: false,
      readMode: false,
      readingGuide: false,
      stopAnimations: false,
      readingMask: false,
      highlightHover: false,
      highlightFocus: false,
      bigCursors: false,
      bigBlackCursor: false,
      bigWhiteCursor: false,
    },
  };
}

// Default state factory
export function createDefaultState(): AuraState {
  return {
    activeProfile: null,
    settings: createDefaultSettings(),
    isOpen: false,
    isHidden: false,
    isAuthenticated: false,
    isLoading: false,
  };
}
